/**
 * SetWhitelabelDomainDialog (TMMPDM-0018) — set or change a tenant's whitelabel domain. On open
 * it loads tmm-api's suggested `hosts` (`GET …/whitelabel-domain/template`, nothing written) as a
 * checklist, all checked; a prefix may be edited. Save PUTs `{domain, hosts}` built from exactly
 * the checked entries (a full replace). Changing an existing domain prefills the domain, and a
 * suggested host matching an existing hostname (same type and app) starts with that hostname's
 * prefix.
 *
 * Errors: `VALIDATION_FAILED` → per-field messages; the pass-through `WHITELABEL_*` codes and
 * `HOSTNAME_ALREADY_REGISTERED` → the backend's message; 502 `SAAS_AUTH_OUTCOME_UNKNOWN` → the
 * parent re-reads (`onOutcomeUnknown`) — never re-sent.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useDiscardChangesGuard, type DiscardChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { ErrorState, SubmitButton } from '@/components/page';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isOutcomeUnknown,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import type {
  WhitelabelDomainDto,
  WhitelabelHostEntryRequest,
  WhitelabelTemplateHost,
} from '@/api/tmm/types';
import { useHostnameTypeLabel } from './useHostnameTypeLabel';

const DOMAIN_MAX = 255;
const PREFIX_MAX = 63;

interface SetWhitelabelDomainDialogProps {
  open: boolean;
  slug: string;
  /** The configured domain when changing it; `null` to set one up. */
  existing: WhitelabelDomainDto | null;
  onClose: () => void;
  onSaved: (saved: WhitelabelDomainDto) => void;
  /** The PUT may or may not have happened — the parent re-reads; nothing is re-sent. */
  onOutcomeUnknown: () => void;
}

export function SetWhitelabelDomainDialog({
  open,
  slug,
  existing,
  onClose,
  onSaved,
  onOutcomeUnknown,
}: SetWhitelabelDomainDialogProps) {
  const { t } = useCopy();
  const [saving, setSaving] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  if (wasOpen !== open) {
    setWasOpen(open);
    if (open) setSaving(false);
  }

  const handleClose = () => {
    setSaving(false);
    onClose();
  };
  const discard = useDiscardChangesGuard(
    existing?.domain ?? t('whitelabel.set.domainName', 'the custom domain'),
    handleClose,
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : discard.requestClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {existing
            ? t('whitelabel.set.changeTitle', 'Change domain')
            : t('whitelabel.set.title', 'Set up a domain')}
        </DialogTitle>
        {open ? (
          <DomainForm
            slug={slug}
            existing={existing}
            discard={discard}
            onSaved={onSaved}
            onOutcomeUnknown={onOutcomeUnknown}
            onSavingChange={setSaving}
          />
        ) : null}
      </Dialog>
      {discard.dialog}
    </>
  );
}

interface HostRow {
  template: WhitelabelTemplateHost;
  prefix: string;
  checked: boolean;
}

function initialRows(
  hosts: WhitelabelTemplateHost[],
  existing: WhitelabelDomainDto | null,
): HostRow[] {
  return hosts.map((template) => {
    const match = existing?.hostnames.find(
      (host) => host.hostnameType === template.hostnameType && host.appId === template.appId,
    );
    return { template, prefix: match?.prefix ?? template.prefix, checked: true };
  });
}

interface DomainFormProps {
  slug: string;
  existing: WhitelabelDomainDto | null;
  /** Esc, the backdrop and Cancel ask first when the form is dirty (TMM-B5). */
  discard: DiscardChangesGuard;
  onSaved: (saved: WhitelabelDomainDto) => void;
  onOutcomeUnknown: () => void;
  onSavingChange: (saving: boolean) => void;
}

/** Mounted only while the dialog is open, so its state resets on every open. */
function DomainForm({
  slug,
  existing,
  discard,
  onSaved,
  onOutcomeUnknown,
  onSavingChange,
}: DomainFormProps) {
  const { t } = useCopy();
  // The prefill is taken once, when the form opens.
  const [prefill] = useState(existing);
  const typeLabel = useHostnameTypeLabel();
  const [domain, setDomain] = useState(prefill?.domain ?? '');
  const [rows, setRows] = useState<HostRow[] | null>(null);
  const [baseRows, setBaseRows] = useState<HostRow[] | null>(null);
  const [templateFailed, setTemplateFailed] = useState(false);
  const [templateCause, setTemplateCause] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setBusy = (value: boolean) => {
    setSubmitting(value);
    onSavingChange(value);
  };

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getWhitelabelTemplate(slug)
      .then((template) => {
        if (!cancelled) {
          const loaded = initialRows(template.hosts, prefill);
          setRows(loaded);
          setBaseRows(loaded);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTemplateCause(err);
          setTemplateFailed(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [slug, prefill]);

  const dirty =
    domain !== (prefill?.domain ?? '') ||
    (rows !== null &&
      baseRows !== null &&
      rows.some(
        (row, i) => row.prefix !== baseRows[i]?.prefix || row.checked !== baseRows[i]?.checked,
      ));
  const { setDirty } = discard;
  useEffect(() => {
    setDirty(dirty);
    return () => setDirty(false);
  }, [dirty, setDirty]);

  const updateRow = (index: number, patch: Partial<HostRow>) =>
    setRows((prev) => prev?.map((row, i) => (i === index ? { ...row, ...patch } : row)) ?? prev);

  const noteLabel = (note: string) =>
    note === 'required' || note === 'optional' ? t(`whitelabel.note.${note}`, note) : note;

  const handleSubmit = async () => {
    if (!rows) return;
    const next: Record<string, string> = {};
    const trimmed = domain.trim();
    if (trimmed === '') {
      next.domain = t('whitelabel.set.domainRequired', 'Domain is required.');
    } else if (trimmed.length > DOMAIN_MAX) {
      next.domain = t('whitelabel.set.domainTooLong', 'Domain must be 255 characters or fewer.');
    }
    const checked = rows.filter((row) => row.checked);
    rows.forEach((row, index) => {
      if (!row.checked) return;
      if (row.prefix.trim() === '') {
        next[`prefix.${index}`] = t('whitelabel.set.prefixRequired', 'Prefix is required.');
      } else if (row.prefix.trim().length > PREFIX_MAX) {
        next[`prefix.${index}`] = t(
          'whitelabel.set.prefixTooLong',
          'Prefix must be 63 characters or fewer.',
        );
      }
    });
    setFieldErrors(next);
    setFormError(
      checked.length === 0
        ? t('whitelabel.set.noneChecked', 'Choose at least one hostname.')
        : null,
    );
    if (Object.keys(next).length > 0 || checked.length === 0) return;

    const hosts: WhitelabelHostEntryRequest[] = checked.map(({ prefix, template }) => ({
      prefix: prefix.trim(),
      hostnameType: template.hostnameType,
      // TMMAPI-0167: `appId` is optional and not nullable; a host with no app omits it.
      ...(template.appId !== null ? { appId: template.appId } : {}),
    }));
    setBusy(true);
    try {
      const saved = await adminApi.putWhitelabel(slug, { domain: trimmed, hosts });
      onSaved(saved);
    } catch (err) {
      if (isOutcomeUnknown(err)) {
        onOutcomeUnknown();
      } else if (
        err instanceof ApiError &&
        (String(err.code).startsWith('WHITELABEL_') ||
          String(err.code) === 'HOSTNAME_ALREADY_REGISTERED')
      ) {
        setFormError(
          errorCopy(
            err,
            t,
            t('whitelabel.set.refused', "That domain can't be used. Check it and try again."),
          ),
        );
      } else if (isValidationFailed(err)) {
        const fields = validationFields(err, t, {
          domain: t(
            'errors.code.WHITELABEL_DOMAIN_INVALID',
            "That domain isn't valid. Check it and try again.",
          ),
        });
        const { domain: domainError, ...others } = fields;
        if (domainError) setFieldErrors({ domain: domainError });
        // The domain is said at its field; a field the form doesn't show is said once, in the banner.
        setFormError(
          Object.keys(others).length > 0 || !domainError
            ? errorCopy(err, t, t('whitelabel.set.saveError', 'Could not save the domain.'))
            : null,
        );
      } else {
        setFormError(
          errorCopy(err, t, t('whitelabel.set.saveError', 'Could not save the domain.')),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('whitelabel.set.domainLabel', 'Domain')}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            error={Boolean(fieldErrors.domain)}
            helperText={
              fieldErrors.domain ??
              t('whitelabel.set.domainHelper', 'The customer’s own domain, e.g. example.com')
            }
            required
            slotProps={{ htmlInput: { maxLength: DOMAIN_MAX } }}
            size="small"
            fullWidth
          />
          <Typography variant="subtitle2" component="h3">
            {t('whitelabel.hostnames', 'Hostnames')}
          </Typography>
          {templateFailed ? (
            <ErrorState
              error={templateCause}
              message={t('whitelabel.set.templateError', 'Could not load the suggested hostnames.')}
            />
          ) : null}
          {!rows && !templateFailed ? <LoadingSpinner /> : null}
          {rows ? (
            <Table size="small" aria-label={t('whitelabel.hostnames', 'Hostnames')}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">{t('whitelabel.set.include', 'Include')}</TableCell>
                  <TableCell>{t('whitelabel.set.prefix', 'Prefix')}</TableCell>
                  <TableCell>{t('whitelabel.col.type', 'Type')}</TableCell>
                  <TableCell>{t('whitelabel.col.app', 'App')}</TableCell>
                  <TableCell>{t('whitelabel.set.note', 'Note')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const name = row.template.prefix;
                  const prefixError = fieldErrors[`prefix.${index}`];
                  return (
                    <TableRow
                      key={`${row.template.hostnameType}:${row.template.appId ?? ''}:${name}`}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={row.checked}
                          onChange={(e) => updateRow(index, { checked: e.target.checked })}
                          slotProps={{
                            input: {
                              'aria-label': t('whitelabel.set.includeHost', 'Include {{prefix}}', {
                                prefix: name,
                              }),
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={row.prefix}
                          onChange={(e) => updateRow(index, { prefix: e.target.value })}
                          disabled={!row.checked}
                          error={Boolean(prefixError)}
                          helperText={prefixError}
                          size="small"
                          slotProps={{
                            htmlInput: {
                              maxLength: PREFIX_MAX,
                              'aria-label': t('whitelabel.set.prefixFor', 'Prefix for {{prefix}}', {
                                prefix: name,
                              }),
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{typeLabel(row.template.hostnameType)}</TableCell>
                      <TableCell>{row.template.appSlug ?? '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={noteLabel(row.template.note)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={discard.requestClose} disabled={submitting}>
          {t('whitelabel.set.cancel', 'Cancel')}
        </Button>
        <SubmitButton onClick={() => void handleSubmit()} busy={submitting} disabled={!rows}>
          {t('whitelabel.set.save', 'Save')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
